import { ChevronDown, LogOut } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthStore } from '@/core/stores/authStore';
import { useLogout } from '@/features/auth/hooks/useLogout';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { getProfileMenuItems } from '@/shared/utils/navbar-menu-items';

export function NavbarProfile() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { logout, isLoggingOut } = useLogout();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      setIsLogoutDialogOpen(false);
    } catch {
      // Backend rejected; toast already shown by interceptor. Keep dialog open
      // so the user can retry or cancel without losing local session state.
    }
  };

  const fullName = `${user?.displayName ?? ''}`.trim();
  const initials = user?.displayName
    ? `${user.displayName?.[0] ?? ''}${user.displayName?.split(' ')[1]?.[0] ?? ''}`
    : '';

  return (
    <div className="flex items-center gap-4">
      <ConfirmDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        title="Cerrar sesión"
        description="¿Estás seguro que deseas cerrar sesión?"
        confirmLabel="Confirmar"
        confirmLoadingLabel="Cerrando sesión..."
        isPending={isLoggingOut}
        cancelLabel="Cancelar"
        onConfirm={handleLogoutConfirm}
        confirmActionType="destructive"
        data-testid="logout-confirm-dialog"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            className="group flex items-center gap-2 cursor-pointer hover:brightness-70"
            data-testid="navbar-profile-trigger"
          >
            <Avatar className="w-10 h-10 cursor-pointer">
              <AvatarFallback>{initials || 'US'}</AvatarFallback>
              <AvatarImage src={user?.avatarUrl} alt="avatar_picture" />
            </Avatar>
            <div className="flex-col text-light text-end hidden md:flex">
              <span className="text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis text-light">
                {fullName || 'Usuario'}
              </span>
            </div>
            <ChevronDown
              className="
                h-4 w-4
                opacity-60
                transition-transform
                duration-300
                ease-in-out
                group-data-[state=open]:rotate-180
              "
            />
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="w-52 p-1 rounded-xl"
          style={{
            background: 'var(--select-background)',
            border: '0.0625rem solid var(--outline)',
          }}
        >
          {getProfileMenuItems().map(({ label, icon: Icon, path }) => (
            <DropdownMenuItem
              key={label}
              onClick={() => navigate(path)}
              className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer"
              data-testid={`navbar-profile-menu-${path.replace(/\//g, '')}`}
            >
              <Icon size={15} className="text-primary flex-shrink-0 group-hover:text-light" />
              <span className="text-sm text-primary group-hover:text-light">{label}</span>
            </DropdownMenuItem>
          ))}

          <div className="my-1 mx-2" style={{ borderTop: '0.0625rem solid var(--outline)' }} />

          <DropdownMenuItem
            onClick={() => setIsLogoutDialogOpen(true)}
            className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer"
            data-testid="navbar-profile-logout"
          >
            <LogOut size={15} className="text-primary flex-shrink-0 group-hover:text-light" />
            <span className="text-sm text-primary group-hover:text-light">Cerrar sesión</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
